import React, { useEffect, useRef, useState } from 'react';
import Map from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import Polygon from '@arcgis/core/geometry/Polygon';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import * as centroidOperator from '@arcgis/core/geometry/operators/centroidOperator';
import esriConfig from '@arcgis/core/config';
import api from '../services/api';
import '@arcgis/core/assets/esri/themes/light/main.css';
import SketchViewModel from '@arcgis/core/widgets/Sketch/SketchViewModel';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import * as locator from '@arcgis/core/rest/locator';

// Polygon.centroid is deprecated as of ArcGIS JS API 4.34, and can return
// undefined for degenerate/non-simple rings (more likely on hand-drawn
// fields than on validated RPA imports) — fall back gracefully either way.
const getCentroid = (polygon) => {
  try {
    return centroidOperator.execute(polygon) || null;
  } catch (e) {
    return null;
  }
};

// Hand-drawn shapes can end up self-intersecting (e.g. dragging a vertex
// across another edge during reshape) — geodesicArea/geodesicLength throw
// on non-simple polygons, which used to abort silently before reaching
// onDrawComplete/onEditComplete (and therefore the save API call). Repair
// via simplify() where possible, and surface a clear error otherwise instead
// of failing silently. Module-level (not just inside the SketchViewModel
// effect) so the Save Edits trigger's no-active-tool fallback can reuse it.
const finalizeGeometry = (rawGeometry) => {
  let geometry = rawGeometry;
  try {
    if (!geometryEngine.isSimple(geometry)) {
      geometry = geometryEngine.simplify(geometry) || geometry;
    }
    const areaHa = geometryEngine.geodesicArea(geometry, "hectares");
    const perimeterM = geometryEngine.geodesicLength(geometry, "meters");
    if (!(areaHa > 0)) throw new Error("Shape has zero or invalid area");
    const geographicGeom = webMercatorUtils.webMercatorToGeographic(geometry);
    return { rings: geographicGeom.rings, area: areaHa, perimeter: perimeterM };
  } catch (e) {
    console.error("Failed to finalize hand-drawn field geometry:", e);
    alert("This shape isn't valid (it may cross over itself). Please adjust it and try again.");
    return null;
  }
};

const GEOCODE_URL = "https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer";

const COLORS = {
  available: { fill: [13, 148, 136, 0.8],   hex: '#0D9488',                  label: 'No bid yet' },
  draft:     { fill: [13, 148, 136, 0.6],   hex: 'rgba(13, 148, 136, 0.6)',  label: 'Draft' },
  pending:   { fill: [245, 158, 11, 0.5],   hex: 'rgba(245, 158, 11, 0.5)',  label: 'Bid submitted' },
  accepted:  { fill: [255, 255, 255, 0.9],  hex: '#FFFFFF',                  label: 'Winning Bid' },
  rejected:  { fill: [15, 23, 42, 0.8],     hex: '#0F172A',                  label: 'Losing Bid' },
  preview:   { fill: [245, 158, 11, 0.4],   hex: '#F59E0B',                  label: 'Preview' }
};

// Field Management renders parcels by land use rather than bid status.
// Matching is a case-insensitive substring test against the RPA-derived
// parcel.name (e.g. "Arable Cropping", "Permanent Grassland").
const LAND_USE_COLORS = {
  woodland:  { fill: [22, 51, 33, 0.85],    hex: '#163321', label: 'Woodland' },
  grassland: { fill: [13, 148, 136, 0.7],   hex: '#0D9488', label: 'Grassland' },
  arable:    { fill: [210, 180, 140, 0.85], hex: '#D2B48C', label: 'Arable' },
  other:     { fill: [203, 213, 225, 0.7],  hex: '#CBD5E1', label: 'Other' }
};

// Field Management: hand-drawn/custom fields get a red boundary regardless of
// land use, so farmers can tell them apart from RPA-imported fields at a glance.
const CUSTOM_FIELD_OUTLINE = { rgb: [220, 38, 38, 1], hex: '#DC2626' };
const isCustomParcel = (parcel) => !!parcel.parcelRef && parcel.parcelRef.startsWith('CUSTOM_');

export const classifyLandUse = (name) => {
  const n = (name || '').toLowerCase();
  if (n.includes('woodland')) return 'woodland';
  if (n.includes('grassland')) return 'grassland';
  if (n.includes('arable')) return 'arable';
  return 'other';
};

// 👇 UPDATED: Added isSelected parameter to control the thick highlight
const getSymbol = (status, isPreview, isSelected) => {
  if (isPreview) return { type: "simple-fill", color: COLORS.preview.fill, outline: { color: [245, 158, 11, 1], width: 2 } };
  
  const color = COLORS[status] ? COLORS[status].fill : COLORS.available.fill;
  
  let outlineColor = [22, 51, 33, 0.8]; 
  let outlineWidth = 1.5;

  if (isSelected) {
      // Surface White for currently selected parcel
      outlineColor = [255, 255, 255, 1]; 
      outlineWidth = 4;
  } else if (status === 'accepted' || status === 'draft' || status === 'pending') {
      outlineColor = [245, 158, 11, 1]; 
      outlineWidth = 2; 
  }
  
  return { type: "simple-fill", color: color, outline: { color: outlineColor, width: outlineWidth } };
};

const MapComponent = ({ 
  onParcelSelect, 
  filterSbi, 
  previewData, 
  bids = [],
  focusAreaZones = [],
  isDrawingMode,
  onDrawComplete,
  editingParcelId,
  onEditComplete,
  selectedParcelId, // 👈 NEW PROP
  homePostcode, // 👈 NEW PROP: centers the blank map before any farm/SBI is loaded
  includeIgnored = false, // Field Management: fetch/render "Ignored" fields too
  zoomTarget = null, // Field Management: { id, nonce } — pan/zoom to one specific parcel
  saveEditTrigger = 0 // Field Management: incrementing nonce — finalize the active Edit Shape reshape
}) => {
  const mapDiv = useRef(null);
  const viewRef = useRef(null);
  const layerRef = useRef(null);
  const hasZoomed = useRef(false);
  const sketchVMRef = useRef(null);

  // Dashboard.js passes these down as fresh function references on every
  // render. Reading them via ref (kept current below) instead of via the
  // Effect 4 dependency array means an unrelated parent re-render can no
  // longer tear down and recreate the SketchViewModel mid-reshape, which
  // silently orphaned any in-progress "Edit Shape" session and made
  // "Save Edits" a no-op.
  const onDrawCompleteRef = useRef(onDrawComplete);
  const onEditCompleteRef = useRef(onEditComplete);
  useEffect(() => { onDrawCompleteRef.current = onDrawComplete; }, [onDrawComplete]);
  useEffect(() => { onEditCompleteRef.current = onEditComplete; }, [onEditComplete]);

  // The graphic clone currently being reshaped via "Edit Shape", or null if
  // no edit is in progress / it has already been saved or discarded. This is
  // the single source of truth for "is there a pending edit to save" — the
  // Save Edits trigger below checks this instead of guessing from
  // SketchViewModel's activeTool/graphics-layer state, so a save that
  // already went through (e.g. the Save button's own click blurring the map
  // and auto-completing the reshape first) is recognised as done rather than
  // re-attempted or alerted on.
  const activeEditGraphicRef = useRef(null);

  const [parcels, setParcels] = useState([]);
  const [viewReady, setViewReady] = useState(false); 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  
  const [postcodeInput, setPostcodeInput] = useState('');

  const handlePostcodeSearch = async (e) => {
      e.preventDefault();
      if (!postcodeInput.trim() || !viewRef.current) return;

      setLoading(true);
      try {
          const results = await locator.addressToLocations(GEOCODE_URL, {
              address: { SingleLine: postcodeInput },
              countryCode: "GB",
              maxLocations: 1
          });

          if (results && results.length > 0) {
              viewRef.current.goTo({ target: results[0].location, zoom: 14 }, { duration: 1000 }); 
          } else {
              alert("Postcode not found. Please check and try again.");
          }
      } catch (err) {
          console.error("Geocoding failed:", err);
          alert("Search failed. Please try again.");
      } finally {
          setLoading(false);
      }
  };

  // --- EFFECT 1: INITIALIZE MAP ---
  useEffect(() => {
    let isMounted = true;
    let clickHandle = null; // 👈 NEW: Track the click event listener
    hasZoomed.current = false; 
    setViewReady(false); 

    const initializeMap = async () => {
      if (viewRef.current) {
        try { viewRef.current.destroy(); } catch(e){}
        viewRef.current = null;
      }

      try {
        setLoading(true);
        const apiKey = process.env.REACT_APP_ARCGIS_API_KEY_AGOL_API_SECRET;
        if (!apiKey) { setError(true); setLoading(false); return; }
        esriConfig.apiKey = apiKey;

        let fetchedData = [];
        if (previewData && previewData.length > 0) fetchedData = previewData;
        else if (filterSbi) {
            const res = await api.get(`/parcels?sbi=${filterSbi}${includeIgnored ? '&includeIgnored=true' : ''}`);
            fetchedData = res.data;
        }

        if (!isMounted) return;
        setParcels(fetchedData);

        // No farm loaded yet: center the blank map on the farmer's postcode at
        // a true 1:10,000 scale instead of leaving it at the generic default.
        let initialView = { center: [0.125, 52.205], zoom: 12 };
        if (fetchedData.length === 0 && homePostcode) {
            try {
                const results = await locator.addressToLocations(GEOCODE_URL, {
                    address: { SingleLine: homePostcode },
                    countryCode: "GB",
                    maxLocations: 1
                });
                if (isMounted && results && results.length > 0) {
                    initialView = { center: results[0].location, scale: 10000 };
                }
            } catch (geoErr) {
                console.warn("Postcode geocode failed, using default map center", geoErr);
            }
        }

        if (!isMounted) return;

        if (mapDiv.current) {
          const map = new Map({ basemap: "topo-vector" });
          const view = new MapView({
              container: mapDiv.current,
              map: map,
              ...initialView,
              ui: { components: ["zoom", "attribution"] }
          });

          // Disable default popups completely
          view.popup.autoOpenEnabled = false;

          const ineligibleMaskLayer = new GraphicsLayer({ title: "Ineligible Mask" });
          const parcelLayer = new GraphicsLayer({ title: "Parcels" });
          const labelLayer = new GraphicsLayer({ title: "Labels", minScale: 20000 });
          const sketchLayer = new GraphicsLayer({ title: "Sketch" });

          // Field Management: clicking a field shows a popup (Auction Bidding
          // has no popup — it relies on the side-panel form for parcel details).
          if (includeIgnored) {
            parcelLayer.popupTemplate = {
              title: '{parcelRef}',
              content: 'Custom Name: {customName}<br/>Land Use: {status}<br/>Area: {area}<br/>Perimeter: {perimeter}'
            };
          }

          // Mask layer added first (bottom of the stack) so it never visually
          // sits on top of parcels — click hit-testing below is scoped to
          // named layers regardless of z-order, so this is purely cosmetic.
          map.addMany([ineligibleMaskLayer, parcelLayer, labelLayer, sketchLayer]);

          viewRef.current = view;
          layerRef.current = { mask: ineligibleMaskLayer, parcels: parcelLayer, labels: labelLayer, sketch: sketchLayer };

          // 👇 UPDATED: Use hitTest on click instead of popup watcher
          clickHandle = view.on("click", async (event) => {
              // Ignore standard clicks if we are actively drawing/editing
              if (sketchVMRef.current && sketchVMRef.current.activeTool) return;

              const response = await view.hitTest(event);
              // Find the first graphic that belongs to our interactive layers
              const parcelResult = response.results.find(r => 
                  r.graphic.layer === layerRef.current.parcels || 
                  r.graphic.layer === layerRef.current.labels
              );

              if (parcelResult && parcelResult.graphic) {
                  onParcelSelect(parcelResult.graphic.attributes);
                  if (includeIgnored) view.popup.open({ location: event.mapPoint, features: [parcelResult.graphic] });
              } else {
                  onParcelSelect(null); // Clicked empty space
                  if (includeIgnored) view.popup.close();
              }
          });

          view.when(() => { if (isMounted) { setViewReady(true); setLoading(false); } });
        }
      } catch (err) { if (isMounted) { setError(true); setLoading(false); } }
    };

    initializeMap();
    return () => { 
        isMounted = false; 
        if (clickHandle) clickHandle.remove(); 
        if (viewRef.current) { try{viewRef.current.destroy()}catch(e){} } 
    };
  }, [filterSbi, previewData, onParcelSelect, homePostcode, includeIgnored]); // Intentionally omitting selectedParcelId here to prevent map reloads

  // --- EFFECT 2: DRAW & ZOOM (Re-runs when selection changes) ---
  useEffect(() => {
    if (!viewReady || !viewRef.current || !layerRef.current || parcels.length === 0) return;

    const parcelLayer = layerRef.current?.parcels;
    const labelLayer = layerRef.current?.labels;
    
    parcelLayer.removeAll();
    if (labelLayer) labelLayer.removeAll();

    try {
        const polyGraphics = [];
        const textGraphics = [];

        parcels.forEach(parcel => {
          const matchingBid = !previewData ? bids.find(b => b.parcel && b.parcel._id === parcel._id) : null;
          const status = matchingBid ? matchingBid.status : 'available';

          const isSelected = parcel._id === selectedParcelId;

          let symbol;
          if (includeIgnored) {
              // Field Management: colour by land use; ignored fields are dimmed
              // and dashed regardless of land use, rather than losing their colour.
              const lu = LAND_USE_COLORS[classifyLandUse(parcel.name)];
              let fill = lu.fill;
              let outline = isCustomParcel(parcel)
                  ? { color: CUSTOM_FIELD_OUTLINE.rgb, width: 2 }
                  : { color: [22, 51, 33, 0.8], width: 1.5 };
              if (parcel.isIgnored) {
                  fill = [fill[0], fill[1], fill[2], fill[3] * 0.4];
                  outline = { color: [100, 116, 139, 1], width: 2, style: 'dash' };
              } else if (isSelected) {
                  outline = { color: [255, 255, 255, 1], width: 4 };
              }
              symbol = { type: 'simple-fill', color: fill, outline };
          } else {
              symbol = getSymbol(status, !!previewData, isSelected);
          }

          const rings = parcel.geometry.coordinates ? parcel.geometry.coordinates : parcel.geometry;
          const polygon = new Polygon({ rings: rings, spatialReference: { wkid: 4326 } });

          const areaVal = parseFloat(parcel.area);
          const areaDisplay = !isNaN(areaVal) ? `${areaVal.toFixed(2)} Ha` : 'Unknown';
          
          let perimeterDisplay = 'Unknown';
          let rawPerimeter = parcel.perimeter || parcel.SHAPE_PERIMETER || parcel.length;
          if (rawPerimeter && !isNaN(parseFloat(rawPerimeter))) {
              perimeterDisplay = `${parseFloat(rawPerimeter).toFixed(1)} m`;
          } else {
              try { perimeterDisplay = `${geometryEngine.planarLength(polygon, "meters").toFixed(1)} m`; } catch (e) {}
          }

          const statusText = includeIgnored
              ? LAND_USE_COLORS[classifyLandUse(parcel.name)].label + (parcel.isIgnored ? ' (Ignored)' : '')
              : (COLORS[status] || COLORS.available).label;

          const sharedAttributes = {
              id: parcel._id,
              name: parcel.name,
              customName: parcel.customName || 'None',
              parcelRef: parcel.parcelRef,
              area: areaDisplay,
              perimeter: perimeterDisplay,
              status: statusText,
              rawStatus: status
          };

          const polygonGraphic = new Graphic({
            geometry: polygon,
            symbol: symbol,
            attributes: sharedAttributes
          });

          const labelText = parcel.customName || parcel.parcelRef;
          const labelCentroid = getCentroid(polygon);

          const textGraphic = labelCentroid ? new Graphic({
              geometry: labelCentroid,
              symbol: {
                  type: "text",
                  color: isSelected ? "#FFFFFF" : "#0F172A", 
                  backgroundColor: isSelected ? "#163321" : null,
                  borderLineColor: isSelected ? "#F59E0B" : null,
                  borderLineSize: isSelected ? 1 : 0,
                  haloColor: isSelected ? null : "rgba(255, 255, 255, 0.7)", 
                  haloSize: isSelected ? 0 : "1px",
                  text: labelText,
                  // 👇 THE FIX: Offset the label 15 points up and 15 points right
                  xoffset: 15,
                  yoffset: 15,
                  font: { 
                      size: isSelected ? 9 : 8, 
                      family: "sans-serif", 
                      weight: "bold" 
                  }
              },
              attributes: sharedAttributes
          }) : null;

          polyGraphics.push(polygonGraphic);
          if (textGraphic) textGraphics.push(textGraphic);
        });

        parcelLayer.addMany(polyGraphics);
        if (labelLayer) labelLayer.addMany(textGraphics);

        if (!hasZoomed.current && polyGraphics.length > 0) {
             hasZoomed.current = true;
             viewRef.current.goTo(polyGraphics).catch((err) => { if (err.name !== "AbortError") console.warn("Zoom Aborted"); });
        }
    } catch (e) { console.error("Update Error", e); }
  }, [bids, parcels, previewData, viewReady, selectedParcelId, includeIgnored]);

  // --- EFFECT 3: DRAW INELIGIBLE-AREA MASK ---
  // When an Auction Focus Area is configured, grey out everywhere outside it
  // by diffing a large fixed WGS84 rectangle against the union of the Focus
  // Area zone polygons — the remainder (with holes where the Focus Area is)
  // gets filled as one translucent mask graphic. No Focus Area configured
  // (focusAreaZones empty) means no mask, map looks exactly as before.
  useEffect(() => {
    if (!viewReady || !layerRef.current?.mask) return;

    const layer = layerRef.current.mask;
    layer.removeAll();

    if (!focusAreaZones || focusAreaZones.length === 0) return;

    try {
        const focusPolygons = focusAreaZones.map(zone => {
            if (!zone.geometry) return null;
            let rings = zone.geometry.coordinates;
            if (zone.geometry.type === 'MultiPolygon') rings = zone.geometry.coordinates.flat(1);
            return new Polygon({ rings, spatialReference: { wkid: 4326 } });
        }).filter(Boolean);

        if (focusPolygons.length === 0) return;

        const unionedFocusArea = geometryEngine.union(focusPolygons);
        const worldRect = new Polygon({
            rings: [[[-170, -85], [170, -85], [170, 85], [-170, 85], [-170, -85]]],
            spatialReference: { wkid: 4326 }
        });
        const outsideFocusArea = geometryEngine.difference(worldRect, unionedFocusArea);
        if (!outsideFocusArea) return;

        layer.add(new Graphic({
            geometry: outsideFocusArea,
            symbol: { type: "simple-fill", color: [100, 116, 139, 0.5], outline: { color: [100, 116, 139, 0], width: 0 } }
        }));
    } catch (e) { console.error("Ineligible mask error", e); }
  }, [focusAreaZones, viewReady]);

  // --- EFFECT 4: INITIALIZE SKETCH VIEW MODEL ---
  useEffect(() => {
    if (!viewReady || !viewRef.current || !layerRef.current?.sketch) return;

    sketchVMRef.current = new SketchViewModel({
      view: viewRef.current,
      layer: layerRef.current.sketch,
      polygonSymbol: {
        type: "simple-fill",
        color: [255, 165, 0, 0.4], 
        outline: { color: [255, 140, 0], width: 2, style: "dash" }
      },
      updateOnGraphicClick: false, 
      defaultUpdateOptions: {
        tool: "reshape", 
        enableRotation: true,
        enableScaling: true,
        multipleSelectionEnabled: false
      }
    });

    sketchVMRef.current.on("create", (event) => {
      if (event.state === "complete") {
        const result = finalizeGeometry(event.graphic.geometry);
        if (result) {
          if (onDrawCompleteRef.current) onDrawCompleteRef.current(result);
          layerRef.current.sketch.removeAll();
        }
      }
    });

    sketchVMRef.current.on("update", (event) => {
      if (event.state === "complete" && !event.aborted && event.graphics.length > 0) {
        const graphic = event.graphics[0];
        // Already handled — either by a previous firing of this same event,
        // or by the Save Edits trigger's own fallback resolving this same
        // graphic first (e.g. it read the geometry directly because
        // activeTool had already gone null by the time it ran). Skip so we
        // don't send a duplicate save for a field that's already persisted.
        if (activeEditGraphicRef.current !== graphic) return;

        const result = finalizeGeometry(graphic.geometry);
        if (result) {
          if (onEditCompleteRef.current) onEditCompleteRef.current(result);
          layerRef.current.sketch.remove(graphic);
          activeEditGraphicRef.current = null;
        } else {
          // Keep the reshape session open on invalid geometry so the user's
          // edits aren't lost — they can adjust the shape and save again.
          sketchVMRef.current.update(graphic);
        }
      }
    });

    return () => { if (sketchVMRef.current) sketchVMRef.current.destroy(); };
  }, [viewReady]);

  // --- EFFECT 5: TOGGLE DRAWING MODE ---
  useEffect(() => {
    if (!sketchVMRef.current) return;

    if (isDrawingMode) {
      onParcelSelect(null);
      sketchVMRef.current.create("polygon");
    } else {
      sketchVMRef.current.cancel();
    }
  }, [isDrawingMode, onParcelSelect]);

  // --- EFFECT 6: TOGGLE EDITING MODE ---
  useEffect(() => {
    if (!sketchVMRef.current || !layerRef.current?.parcels || !layerRef.current?.sketch) return;

    if (editingParcelId) {
      const graphicToEdit = layerRef.current.parcels.graphics.find(
          g => g.attributes && g.attributes.id === editingParcelId
      );
      
      if (graphicToEdit) {
        const graphicClone = graphicToEdit.clone();
        graphicClone.symbol = {
            type: "simple-fill",
            color: [255, 165, 0, 0.4], 
            outline: { color: [255, 140, 0], width: 2, style: "dash" }
        };

        layerRef.current.sketch.add(graphicClone);
        graphicToEdit.visible = false;
        activeEditGraphicRef.current = graphicClone; // fresh edit session — nothing saved yet
        sketchVMRef.current.update(graphicClone);
      }
    } else {
      if (sketchVMRef.current && sketchVMRef.current.activeTool) {
          sketchVMRef.current.cancel();
      }
      if (layerRef.current?.parcels) {
          layerRef.current.parcels.graphics.forEach(g => { g.visible = true; });
      }
      if (layerRef.current?.sketch) {
          layerRef.current.sketch.removeAll();
      }
      activeEditGraphicRef.current = null; // session ended (saved or discarded) — nothing pending
    }
  }, [editingParcelId]);

  // --- EFFECT 7: EXPLICIT "SAVE EDITS" TRIGGER (Field Management Edit Shape) ---
  // Finalizes the in-progress reshape; the Effect 4 "update" handler above
  // picks up the resulting "complete" event and calls onEditComplete.
  useEffect(() => {
    if (!saveEditTrigger) return; // initial mount — no save requested yet

    const graphic = activeEditGraphicRef.current;
    if (!graphic) {
      // Nothing pending: either no edit session was ever started, or it was
      // already saved (e.g. clicking this Save button blurred the map first
      // and SketchViewModel auto-completed the reshape, which the Effect 4
      // "update" handler already processed) or discarded. Either way there's
      // nothing left to do and nothing went wrong — stay silent.
      return;
    }

    const vm = sketchVMRef.current;
    if (vm && vm.activeTool) {
      vm.complete(); // Effect 4's "update" handler finalizes and clears the ref
      return;
    }

    // activeTool is already null but our tracked graphic hasn't been cleared
    // yet — the auto-complete race described above, just not resolved by the
    // "update" event handler in time. Read its geometry directly instead of
    // treating this as a failure.
    const result = finalizeGeometry(graphic.geometry);
    if (result) {
      if (onEditCompleteRef.current) onEditCompleteRef.current(result);
      if (layerRef.current?.sketch) layerRef.current.sketch.remove(graphic);
      activeEditGraphicRef.current = null;
    }
    // On invalid geometry, finalizeGeometry already alerted the user and we
    // leave activeEditGraphicRef set so they can adjust the shape and retry.
  }, [saveEditTrigger]);

  // --- EFFECT 8: ZOOM TO A SPECIFIC PARCEL (Field Management "magnifying glass") ---
  useEffect(() => {
    if (!viewReady || !viewRef.current || !layerRef.current?.parcels || !zoomTarget) return;

    const graphic = layerRef.current.parcels.graphics.find(g => g.attributes && g.attributes.id === zoomTarget.id);
    const target = graphic ? getCentroid(graphic.geometry) : null;
    if (target) {
      viewRef.current.goTo({ target, scale: 5000 }, { duration: 600 })
        .catch((err) => { if (err.name !== "AbortError") console.warn("Zoom to field aborted"); });
    } else if (graphic) {
      viewRef.current.goTo(graphic, { duration: 600 })
        .catch((err) => { if (err.name !== "AbortError") console.warn("Zoom to field aborted"); });
    }
  }, [zoomTarget, viewReady]);

  // --- RENDER ---
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {loading && <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(255,255,255,0.8)', zIndex: 10, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>Loading...</div>}
      {error && <div style={{ padding: '20px', color: 'red' }}>Map Error.</div>}
      
      <div ref={mapDiv} style={{ width: '100%', height: '100%', padding: 0 }} />

      <div style={styles.searchContainer}>
          <form onSubmit={handlePostcodeSearch} style={{ display: 'flex', gap: '8px' }}>
              <input 
                  type="text" 
                  placeholder="Postcode..." 
                  value={postcodeInput} 
                  onChange={(e) => setPostcodeInput(e.target.value)} 
                  style={styles.searchInput}
              />
              <button type="submit" style={styles.searchBtn}>Zoom</button>
          </form>
      </div>

      {!loading && !previewData && (
        <div style={styles.legendContainer}>
            <h5 style={{margin:'0 0 5px 0', fontSize:'0.85rem'}}>Legend</h5>

            {includeIgnored ? (
              <>
                {Object.keys(LAND_USE_COLORS).map(key => (
                    <div key={key} style={styles.legendItem}>
                        <span style={{ ...styles.colorBox, backgroundColor: LAND_USE_COLORS[key].hex, border: '1px solid #ccc' }}></span>
                        <span>{LAND_USE_COLORS[key].label}</span>
                    </div>
                ))}
                <div style={styles.legendItem}>
                    <span style={{ ...styles.colorBox, backgroundColor: 'transparent', border: '2px dashed #64748B' }}></span>
                    <span>Ignored</span>
                </div>
                <div style={styles.legendItem}>
                    <span style={{ ...styles.colorBox, backgroundColor: 'transparent', border: `2px solid ${CUSTOM_FIELD_OUTLINE.hex}` }}></span>
                    <span>Hand-Drawn Boundary</span>
                </div>
              </>
            ) : (
              <>
                {Object.keys(COLORS).filter(k => k !== 'preview').map(key => {
                    const needsAmberOutline = key === 'accepted' || key === 'draft';
                    return (
                        <div key={key} style={styles.legendItem}>
                            <span style={{
                                ...styles.colorBox,
                                backgroundColor: COLORS[key].hex,
                                border: needsAmberOutline ? '2px solid #F59E0B' : '1px solid #ccc'
                            }}></span>
                            <span>{COLORS[key].label}</span>
                        </div>
                    );
                })}
                {focusAreaZones && focusAreaZones.length > 0 && (
                    <div style={styles.legendItem}>
                        <span style={{ ...styles.colorBox, backgroundColor: 'rgba(100, 116, 139, 0.5)', border: '1px solid #ccc' }}></span>
                        <span>Ineligible area</span>
                    </div>
                )}
                {/* 👇 THE FIX: Replaced the box with a mini version of the map label */}
                <div style={styles.legendItem}>
                    <span style={{
                        backgroundColor: '#163321',
                        color: '#FFFFFF',
                        border: '1px solid #F59E0B',
                        padding: '1px 4px',
                        borderRadius: '2px',
                        marginRight: '8px',
                        fontSize: '9px',
                        fontWeight: 'bold',
                        display: 'inline-block',
                        fontFamily: 'sans-serif'
                    }}>
                        XY 1234
                    </span>
                    <span>Selected Field</span>
                </div>
              </>
            )}
        </div>
      )}
    </div>
  );
};

const styles = {
    legendContainer: { position: 'absolute', bottom: '20px', left: '20px', backgroundColor: '#FFFFFF', color: '#0F172A', padding: '10px', borderRadius: '5px', boxShadow: '0 2px 6px rgba(0,0,0,0.3)', fontSize: '0.75rem', fontFamily: '"Inter", sans-serif', zIndex: 5, minWidth: '120px' },
    legendItem: { display: 'flex', alignItems: 'center', marginBottom: '4px' },
    colorBox: { width: '12px', height: '12px', marginRight: '8px', borderRadius: '2px', border: '1px solid #ccc' },
    searchContainer: { position: 'absolute', top: '15px', left: '65px', backgroundColor: '#FFFFFF', padding: '8px', borderRadius: '6px', boxShadow: '0 2px 6px rgba(0,0,0,0.2)', zIndex: 5 },
    searchInput: { padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.85rem', fontFamily: '"Inter", sans-serif', width: '140px', color: '#0F172A' },
    searchBtn: { padding: '8px 12px', backgroundColor: '#0D9488', color: '#FFFFFF', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold', fontFamily: '"Inter", sans-serif' }
};

export default MapComponent;